import {
  FINANCE_FEDERAL_CHAMBERS,
  FINANCE_JUDICIAL_OFFICE_TIERS,
  FINANCE_LOUISIANA_OFFICE_LEVELS,
  FINANCE_NEW_JERSEY_OFFICE_LEVELS,
  FINANCE_NON_FEDERAL_OFFICE_LEVELS,
  FINANCE_OFFICE_LEVELS,
} from '@signalvote/shared';

// The office levels, their per-jurisdiction lists and their order come from the generated
// OfficeLevels; only the treasurer-facing labels live here. Federal is
// auto-derived from the federal jurisdiction, so it is never an option. Louisiana (R.S. 18:1483)
// and New Jersey replace the generic list. The judicial tier (Mississippi § 23-15-1021) and the
// federal chamber refine the contribution-limit lookup and are optional, so their pickers keep
// an explicit empty choice.
type OfficeLevelOption = { value: string; label: string; helper?: string };
type OfficeLevelLabel = Omit<OfficeLevelOption, 'value'>;
type PickableOfficeLevel = Exclude<
  (typeof FINANCE_OFFICE_LEVELS)[keyof typeof FINANCE_OFFICE_LEVELS],
  typeof FINANCE_OFFICE_LEVELS.Federal
>;

const L = FINANCE_OFFICE_LEVELS;
export const OFFICE_LEVEL_LABELS: Record<PickableOfficeLevel, OfficeLevelLabel> = {
  [L.Statewide]: { label: 'Statewide' },
  [L.Legislative]: { label: 'State Legislature' },
  [L.County]: { label: 'County' },
  [L.Municipal]: { label: 'City / Municipal' },
  [L.SchoolBoard]: { label: 'School Board' },
  [L.Judicial]: { label: 'Judicial' },
  [L.Gubernatorial]: { label: 'Governor / Lieutenant Governor' },
  [L.FireDistrict]: { label: 'Fire District' },
  [L.JudicialTrial]: {
    label: 'Trial court',
    helper: 'County, Circuit, Chancery, or Justice Court. $2,500 per election, per contributor.',
  },
  [L.JudicialAppellate]: {
    label: 'Court of Appeals / Supreme Court',
    helper: 'Statewide appellate seats. $5,000 per election, per contributor.',
  },
  [L.House]: { label: 'U.S. House' },
  [L.Senate]: { label: 'U.S. Senate' },
  [L.Presidential]: { label: 'President' },
  // Statute citations let a treasurer (or their lawyer) verify the tier against the law. The
  // raw `anyOther` token is never surfaced as copy.
  [L.Major]: {
    label: 'Major office',
    helper:
      'Statewide office, Public Service Commission, Supreme Court, Court of Appeal, BESE, or a district over 250,000 people. (R.S. 18:1483)',
  },
  [L.District]: {
    label: 'District office',
    helper:
      'State Legislature, a parish-wide office, or a district of 35,000–250,000 people. (R.S. 18:1483)',
  },
  // R.S. 18:1484: either trigger requires filing. The personal-funds carve-out applies only to
  // the receipts test.
  [L.AnyOther]: {
    label: 'Any other office',
    helper:
      "Any office that isn't major or district. Files only if it receives more than $5,000 in contributions (not counting the candidate's own money) or spends more than $5,000. (R.S. 18:1484)",
  },
};

function isPickableOfficeLevel(value: string): value is PickableOfficeLevel {
  return Object.prototype.hasOwnProperty.call(OFFICE_LEVEL_LABELS, value);
}

// The generated per-jurisdiction lists are typed `string[]`, so tsc cannot prove each entry is
// labelled. An unlabelled one shows its raw token rather than crashing the card at module load;
// the officeLevelOptions test fails on that fallback.
function officeLevelOptions(levels: readonly string[]): OfficeLevelOption[] {
  return levels.map((value) => ({
    value,
    ...(isPickableOfficeLevel(value) ? OFFICE_LEVEL_LABELS[value] : { label: value }),
  }));
}

export const OFFICE_LEVEL_OPTIONS = officeLevelOptions(FINANCE_NON_FEDERAL_OFFICE_LEVELS);
export const JUDICIAL_TIER_OPTIONS = officeLevelOptions(FINANCE_JUDICIAL_OFFICE_TIERS);
export const FEDERAL_CHAMBER_OPTIONS = officeLevelOptions(FINANCE_FEDERAL_CHAMBERS);
export const LA_OFFICE_LEVEL_OPTIONS = officeLevelOptions(FINANCE_LOUISIANA_OFFICE_LEVELS);
export const NJ_OFFICE_LEVEL_OPTIONS = officeLevelOptions(FINANCE_NEW_JERSEY_OFFICE_LEVELS);
